import { useToast } from '@afterpaytouch/core'
import { titleCase } from '@afterpay/utils'
import { CreditCard } from '@afterpaytouch/portal-api/consumer/account/creditcards/types'
import { useChangeBillingAgreementPaymentMethodMutation, useConsumerCountry, useApplePay, useGetAccountData } from '../../state'
import { TrackingEvent } from '../../model/amplitude'
import { useAmplitudeWithEnduringEventProperties } from '../../integrations/amplitude'
import { ApplePayHelperResolve, HelperType } from '../../state/applepay/helper'
import { deviceDetails } from '../../utils/device'
import { maskCreditCardNumber } from '../../utils/card'
import { UseUpdatePaymentBusinessLogicProps, UseUpdatePaymentBusinessLogicReturn } from '../ChangePaymentMethod'
import { BankAccountDetails, CashAppPayDetail } from '@afterpay/types'
import { mapContryCodeToCurrency } from '../Money/utils'

export const useUpdatePaymentBusinessLogic = ({
  agreementId,
  setError,
  t,
  modalOnClose,
}: UseUpdatePaymentBusinessLogicProps): UseUpdatePaymentBusinessLogicReturn => {
  const [changePaymentMethod] = useChangeBillingAgreementPaymentMethodMutation()
  const setToast = useToast()
  const { logEvent } = useAmplitudeWithEnduringEventProperties()
  const { applePayInstance } = useApplePay()
  const countryCode = useConsumerCountry()
  const accountData = useGetAccountData()
  const updatePaymentMethodToApplePay = async (): Promise<void> => {
    const total: ApplePayJS.ApplePayLineItem = {
      label: `${t('common:brandName')}`,
      amount: '0.00',
      type: 'pending',
    }

    applePayInstance.initialise(
      {
        // @ts-ignore: OPERATION BLEED STOPPER
        countryCode,
        // @ts-ignore: OPERATION BLEED STOPPER
        currencyCode: mapContryCodeToCurrency(countryCode) ?? 'AUD',
        // @ts-ignore: OPERATION BLEED STOPPER
        supportedCountries: [countryCode],
        total,
        accountData,
      },
      logEvent,
      HelperType.UPDATE_PAYMENT
    )

    try {
      const response: ApplePayHelperResolve = await applePayInstance.startSession()

      const payload = {
        paymentSourceId: response.applePayToken.paymentSourceId,
        deviceDetails: deviceDetails(),
      }

      try {
        await changePaymentMethod({
          // @ts-ignore: OPERATION BLEED STOPPER
          agreementId,
          body: payload,
        }).unwrap()

        setError(null)
        modalOnClose()

        // Set apple pay session as success
        // @ts-ignore: OPERATION BLEED STOPPER
        response.session.completePayment({ status: 0 })

        setToast({
          message: t('orders:orderPaymentMethod:modal:tab:paymentMethods:success', {
            paymentMethod: 'Apple Pay',
          }),
          kind: 'Success',
          testNameSpace: 'agreement-toast',
        })
      } catch (e) {
        const errorCode = e?.data?.errorCode ?? 'unspecified_error'
        setError({ errorCode })
        // @ts-ignore: OPERATION BLEED STOPPER
        response.session.completePayment({ status: 1 })
      }
    } catch (e) {
      const errorCode = e?.data?.errorCode ?? 'unspecified_error'
      setError({ errorCode })
    }
  }

  const updateCreditCard = async (paymentMethod: CreditCard): Promise<void> => {
    try {
      await changePaymentMethod({
        // @ts-ignore: OPERATION BLEED STOPPER
        agreementId,
        body: {
          cardId: paymentMethod.id,
        },
      }).unwrap()

      setToast({
        message: t('orders:orderPaymentMethod:modal:tab:paymentMethods:success', {
          paymentMethod: `${titleCase(paymentMethod.cardBrand)} ${maskCreditCardNumber(paymentMethod.truncatedNumber ?? paymentMethod.maskedPan)}`,
        }),
        kind: 'Success',
        testNameSpace: 'agreement-toast',
      })

      setError(null)
      modalOnClose()
    } catch (e) {
      // if no error code, put unspecified_error
      const errorCode = e?.data?.errorCode ?? 'unspecified_error'
      logEvent(TrackingEvent.UPDATE_PAYMENT_METHOD_IN_BILLING_AGREEMENT_FAILED, {
        error: errorCode,
      })
      setError({ errorCode, options: { cardBrand: paymentMethod.cardBrand } })
    }
  }

  const updateBankAccount = async (paymentMethod: BankAccountDetails): Promise<void> => {
    try {
      await changePaymentMethod({
        // @ts-ignore: OPERATION BLEED STOPPER
        agreementId,
        body: {
          bankAccountId: paymentMethod.id.toString(),
        },
      }).unwrap()

      setToast({
        message: t('orders:orderPaymentMethod:modal:tab:paymentMethods:success', {
          paymentMethod: `${titleCase(paymentMethod.issuingBank)} ${maskCreditCardNumber(paymentMethod.numberMask)}`,
        }),
        kind: 'Success',
        testNameSpace: 'agreement-toast',
      })

      setError(null)
      modalOnClose()
    } catch (e) {
      // if no error code, put unspecified_error
      const errorCode = e?.data?.errorCode ?? 'unspecified_error'
      logEvent(TrackingEvent.UPDATE_PAYMENT_METHOD_IN_ORDER_FAILED, {
        error: errorCode,
      })
      setError({ errorCode, options: { cardBrand: paymentMethod } })
    }
  }

  const updateCashApp = async (paymentMethod: CashAppPayDetail): Promise<void> => {
    try {
      // await changePaymentMethod({
      //   agreementId,
      //   body: {
      //     bankAccountId: paymentMethod.id.toString(),
      //   },
      // }).unwrap()

      // TODO: @james

      setToast({
        message: t('orders:orderPaymentMethod:modal:tab:paymentMethods:success', {
          paymentMethod: `${maskCreditCardNumber(paymentMethod.cashtag)}`,
        }),
        kind: 'Success',
      })

      setError(null)
      modalOnClose()
    } catch (e) {
      // if no error code, put unspecified_error
      const errorCode = e?.data?.errorCode ?? 'unspecified_error'
      logEvent(TrackingEvent.UPDATE_PAYMENT_METHOD_IN_ORDER_FAILED, {
        error: errorCode,
      })
      setError({ errorCode, options: { cardBrand: paymentMethod } })
    }
  }

  return {
    updatePaymentMethodToApplePay,
    updateCreditCard,
    updateBankAccount,
    updateCashApp,
  }
}
